import React, {useEffect, useRef} from 'react';
import ImageWithFallback from "../Common/ImageWithFallback";
import {IconButton, PrimaryButton, Spinner, SpinnerSize, TextField, Toggle, TooltipHost, Icon} from '@fluentui/react';
import {ISavedSearch, ISavedSearchRef, ISearchRequest} from '../../models/Search';
import {useGetSavedSearchRefsQuery, useLazyGetSavedSearchQuery, useSaveSearchMutation} from '../../store/endpoints/SavedSearchEndpoints';
import SaveSearchPanel from './SaveSearchPanel';
import SavedSearchPeek from './SavedSearchPeek';

interface ISavedSearchRefSearchTableProps {
    selectedSaveSearch?: ISavedSearchRef;
    showResults?: boolean;
    onResultsCountChange?: (count: number) => void;
    onSelectedSearchChange?: (search: ISavedSearchRef | undefined) => void;
    onHide?: () => void;
    onClear?: (clearFn: () => void) => void;
    showOnlyMine?: boolean;
    currentUsername?: string;
    currentCriteria?: ISearchRequest;
    clearToken?: number;
}

const SavedSearchRefSearchTable: React.FC<ISavedSearchRefSearchTableProps> = (props) => {
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);
    const [leftHover, setLeftHover] = React.useState(false);
    const [rightHover, setRightHover] = React.useState(false);
    const [keyword, setKeyword] = React.useState<string>('');
    const [debouncedKeyword, setDebouncedKeyword] = React.useState<string>('');
    const [showOnlyMine, setShowOnlyMine] = React.useState<boolean>(props.showOnlyMine ?? false);
    const [detailsSearch, setDetailsSearch] = React.useState<ISavedSearch | undefined>();
    const [detailsPanelOpen, setDetailsPanelOpen] = React.useState(false);
    const [createHover, setCreateHover] = React.useState(false);
    const [creating, setCreating] = React.useState(false);

    const savedSearchRefs = useGetSavedSearchRefsQuery();
    const [fetchSavedSearch] = useLazyGetSavedSearchQuery();
    const [saveSearch] = useSaveSearchMutation();
    
    const [clearToken, setClearToken] = React.useState<number>();
    
    React.useEffect(() => {
        if(props.clearToken && !clearToken){
            setClearToken(props.clearToken);
        }
        else if(props.clearToken && clearToken && props.clearToken != clearToken){
            props.onSelectedSearchChange?.(undefined);
        }

    }, [props.clearToken])
    

    React.useEffect(() => {
        const handler = setTimeout(() => setDebouncedKeyword(keyword), 500);
        return () => clearTimeout(handler);
    }, [keyword]);

    const filteredData = React.useMemo(() => {
        let data = savedSearchRefs.data ?? [];
        if (showOnlyMine && props.currentUsername) {
            data = data.filter(s => s.username === props.currentUsername);
        }
        if (debouncedKeyword.trim().length > 0) {
            const tokens = debouncedKeyword.toLowerCase().split(/\s+/).filter(t => t.length > 0);
            data = data.filter(a =>
                tokens.some(token =>
                    a.name?.toLowerCase().includes(token) || a.username?.toLowerCase().includes(token)
                )
            );
        }
        return data;
    }, [savedSearchRefs.data, debouncedKeyword, showOnlyMine, props.currentUsername]);

    useEffect(() => {
        props.onResultsCountChange?.(filteredData.length);
    }, [filteredData.length]);

    useEffect(() => {
        if (props.clearToken) {
            props.onSelectedSearchChange?.(undefined);
        }
    }, [props.clearToken]);

    useEffect(() => {
        if (props.selectedSaveSearch?.id && !filteredData.some(s => s.id === props.selectedSaveSearch?.id)) {
            props.onSelectedSearchChange?.(undefined);
        }
    }, [filteredData]);

    useEffect(() => {
        checkScroll();
        const handleResize = () => checkScroll();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [filteredData]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const cardWidth = 200;
    const gap = 54;

    const checkScroll = () => {
        const el = scrollContainerRef.current;
        if (el) {
            setCanScrollLeft(el.scrollLeft > 0);
            setCanScrollRight(el.scrollLeft + el.clientWidth + 1 < el.scrollWidth);
        }
    };

    const scrollByCard = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -(cardWidth + gap) : (cardWidth + gap),
                behavior: 'smooth'
            });
        }
    };

    const openDetails = (item: ISavedSearchRef) => {
        if (!item.id) return;
        fetchSavedSearch({id: item.id}).then((result) => {
            if (result.data) {
                setDetailsSearch(result.data);
                setDetailsPanelOpen(true);
            }
        });
    };

    const [updatingId, setUpdatingId] = React.useState<string | undefined>();
    const [applyingId, setApplyingId] = React.useState<string | undefined>();

    React.useEffect(() => {
        if (applyingId && props.selectedSaveSearch?.id === applyingId) {
            setApplyingId(undefined);
        }
    }, [props.selectedSaveSearch?.id, applyingId]);

    // ...existing code...

    const updateSearch = (item: ISavedSearchRef) => {
        if (!item.id || !props.currentCriteria) return;
        setUpdatingId(item.id);
        fetchSavedSearch({ id: item.id }).then((r) => {
            if (!r.data) { setUpdatingId(undefined); return; }
            saveSearch({ savedSearch: { ...r.data, criteria: props.currentCriteria } })
                .unwrap()
                .finally(() => setUpdatingId(undefined));
        });
    };
    const createNew = () => {
        if (!props.currentCriteria) return;
        setCreating(true);
        saveSearch({ savedSearch: { name: 'New Saved Search', criteria: props.currentCriteria } })
            .unwrap()
            .then((result) => {
                if (result?.id) {
                    fetchSavedSearch({ id: result.id }).then((r) => {
                        if (r.data) {
                            setDetailsSearch(r.data);
                            setDetailsPanelOpen(true);
                        }
                    });
                }
            })
            .finally(() => setCreating(false));
    };

    return (
        <div>
            {props.showResults &&
                <div style={{
                    position: 'relative',
                    background: '#f5f6fa',
                    borderRadius: 8,
                    padding: 24,
                    marginBottom: 24
                }}>
                    <div style={{display: 'flex'}}>
                        <IconButton
                            iconProps={{iconName: 'Cancel'}}
                            ariaLabel="Close saved searches"
                            title="Close"
                            style={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 2,
                                background: 'transparent'
                            }}
                            onClick={() => props.onHide?.()}
                        />
                        <IconButton
                            iconProps={{iconName: 'Refresh'}}
                            ariaLabel="Refresh saved searches"
                            title="Refresh"
                            disabled={savedSearchRefs.isFetching}
                            style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 2,
                                background: 'transparent'
                            }}
                            onClick={() => savedSearchRefs.refetch()}
                        />
                        <div style={{display: 'flex', alignItems: 'center', gap: 16, marginLeft: 40, marginBottom: 16}}>
                            <h3 style={{margin: 0}}><Icon iconName='SearchBookmark' styles={{root: {fontSize: 16, marginRight: 8, color: '#789c2f', verticalAlign: 'text-bottom'}}} aria-hidden />Saved Searches</h3>
                            <TextField
                                placeholder="Search..."
                                ariaLabel="Filter saved searches"
                                value={keyword}
                                onChange={(_, v) => setKeyword(v || '')}
                                styles={{root: {width: 250}}}
                            />
                            <Toggle
                                checked={showOnlyMine}
                                onChange={(_, checked) => setShowOnlyMine(checked ?? false)}
                                onText="Show Just Mine"
                                offText="Hide Not Mine"
                                styles={{root: {marginBottom: 0}}}
                            />
                        </div>
                    </div>

                    <div style={{position: 'relative', width: '100%'}}>
                        {/* Left Arrow */}
                        {canScrollLeft &&
                            <IconButton
                                iconProps={{iconName: 'ChevronLeft'}}
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 50,
                                    height: 280,
                                    zIndex: 1,
                                    background: leftHover ? 'rgb(120, 156, 47, 0.75)' : 'rgba(255,255,255,0.7)'
                                }}
                                onClick={() => scrollByCard('left')}
                                ariaLabel="Scroll left"
                                onMouseEnter={() => setLeftHover(true)}
                                onMouseLeave={() => setLeftHover(false)}
                            />
                        }

                        {/* Scrollable Container */}
                        <div
                            ref={scrollContainerRef}
                            onScroll={checkScroll}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                height: '300px',
                                justifyContent: 'flex-start',
                                overflowX: 'auto',
                                width: '100%',
                                gap: '10px',
                                scrollBehavior: 'smooth'
                            }}
                        >
                            {/* Create New Pod */}
                            <div
                                onMouseEnter={() => setCreateHover(true)}
                                onMouseLeave={() => setCreateHover(false)}
                                onClick={createNew}
                                style={{
                                    border: createHover ? '2px dashed rgb(120, 156, 47)' : '2px dashed #ddd',
                                    padding: '10px',
                                    margin: '10px',
                                    cursor: props.currentCriteria ? 'pointer' : 'default',
                                    width: createHover ? '200px' : '48px',
                                    minWidth: createHover ? '200px' : '48px',
                                    maxWidth: createHover ? '200px' : '48px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 8,
                                    background: createHover ? 'rgba(120, 156, 47, 0.05)' : 'transparent',
                                    borderRadius: 4,
                                    transition: 'width 0.2s, min-width 0.2s, max-width 0.2s, border 0.15s, background 0.15s',
                                    opacity: props.currentCriteria ? 1 : 0.4,
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                }}
                                title={props.currentCriteria ? 'Save current search' : 'No active search criteria'}
                            >
                                <IconButton
                                    iconProps={{ iconName: creating ? 'Sync' : 'AddBookmark' }}
                                    disabled={!props.currentCriteria || creating}
                                    styles={{
                                        root: { width: 28, height: 28, pointerEvents: 'none', flexShrink: 0 },
                                        icon: { fontSize: 18, color: createHover ? 'rgb(120, 156, 47)' : '#aaa' }
                                    }}
                                />
                                {createHover && (
                                    <span style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: 'rgb(120, 156, 47)',
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {creating ? 'Creating...' : 'Save Current Search'}
                                    </span>
                                )}
                            </div>

                            {filteredData.map((item) => {
                                const isSelected = props.selectedSaveSearch?.id === item.id;
                                return (
                                    <div
                                        key={item.id}
                                        style={{
                                            borderRadius: '5px',
                                            backgroundColor: isSelected ? 'rgb(220, 237, 200)' : '#fff',
                                            border: isSelected ? '4px solid rgb(120, 156, 47)' : '1px solid #ccc',
                                            padding: '10px',
                                            margin: '10px',
                                            cursor: 'pointer',
                                            minWidth: '200px',
                                            maxWidth: '200px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            position: 'relative',
                                        }}
                                        onClick={() => {
                                            if (!isSelected) {
                                                setApplyingId(item.id);
                                            }
                                            props.onSelectedSearchChange?.(item);
                                        }}
                                    >
                                        {/* Applying overlay */}
                                        {applyingId === item.id && (
                                            <div style={{
                                                position: 'absolute',
                                                inset: 0,
                                                borderRadius: 5,
                                                background: 'rgba(255,255,255,0.82)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 8,
                                                zIndex: 5,
                                            }}>
                                                <Spinner size={SpinnerSize.medium} />
                                                <span style={{fontSize: 13, fontWeight: 600, color: 'rgb(120,156,47)'}}>Applying…</span>
                                            </div>
                                        )}
                                        {/* Icon area */}
                                        <div style={{
                                            height: '100px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <ImageWithFallback src={item.image} alt={item.name}
                                                               style={{width: '80%', height: 'auto', maxHeight: '100px', objectFit: 'contain', margin: '0 auto'}}/>
                                        </div>

                                        {/* Name; hovering peeks at what the search looks for */}
                                        <SavedSearchPeek savedSearchId={item.id} name={item.name}>
                                            <div style={{
                                                fontWeight: 600,
                                                fontSize: 14,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                marginBottom: 4,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                            }}>
                                                <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0}}>{item.name}</span>
                                                {/* The eye says "hover me": the peek at this search's criteria. */}
                                                <Icon iconName='View' aria-label='Peek at the search criteria' styles={{root: {fontSize: 12, color: '#789c2f', flexShrink: 0, cursor: 'help'}}} />
                                                {isSelected && item.username === props.currentUsername && props.currentCriteria && (
                                                    <TooltipHost content="Update search with current criteria">
                                                        <IconButton
                                                            iconProps={{ iconName: updatingId === item.id ? 'Sync' : 'Save' }}
                                                            disabled={updatingId === item.id}
                                                            onClick={(e) => { e.stopPropagation(); updateSearch(item); }}
                                                            styles={{
                                                                root: {
                                                                    width: 22,
                                                                    height: 22,
                                                                    flexShrink: 0,
                                                                    color: 'rgb(120, 156, 47)',
                                                                    opacity: 0.7,
                                                                },
                                                                icon: { fontSize: 12, color: 'rgb(120, 156, 47)' },
                                                                rootHovered: { opacity: 1, background: 'rgba(120,156,47,0.1)' },
                                                            }}
                                                        />
                                                    </TooltipHost>
                                                )}
                                            </div>
                                        </SavedSearchPeek>

                                        {/* Metadata */}
                                        <div style={{fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 6}}>
                                            {item.username && <div>By: {item.username}</div>}
                                            {item.timesSearched != null && <div>Used: {item.timesSearched}x</div>}
                                            {item.shared && <div style={{color: 'rgb(120, 156, 47)'}}>Shared</div>}
                                        </div>

                                        <PrimaryButton
                                            iconProps={{iconName: 'edit'}}
                                            text={"Details"}
                                            ariaLabel={`Saved search details for ${item.name}`}
                                            style={{width: '100%'}}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                openDetails(item)
                                            }}
                                        />
                                    </div>
                                );
                            })}

                            {filteredData.length === 0 && !savedSearchRefs.isLoading && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    color: '#888',
                                    fontSize: 14
                                }}>
                                    No saved searches found.
                                </div>
                            )}
                        </div>

                        {/* Right Arrow */}
                        {canScrollRight &&
                            <IconButton
                                iconProps={{iconName: 'ChevronRight'}}
                                style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 50,
                                    height: 280,
                                    zIndex: 1,
                                    background: rightHover ? 'rgb(120, 156, 47, 0.75)' : 'rgba(255,255,255,0.7)'
                                }}
                                onClick={() => scrollByCard('right')}
                                ariaLabel="Scroll right"
                                onMouseEnter={() => setRightHover(true)}
                                onMouseLeave={() => setRightHover(false)}
                            />
                        }
                    </div>
                </div>
            }
            {detailsPanelOpen && detailsSearch && (
                <SaveSearchPanel
                    closer={() => setDetailsPanelOpen(false)}
                    savedSearchId={detailsSearch?.id}
                />
            )}
        </div>
    );
};

export default SavedSearchRefSearchTable;

